import { CommonModule } from '@angular/common';
import { Component, Signal, computed, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PairingsService, PairingsSwapGroupOption, PairingsSwapHistoryEntry, PairingsSwapPlayerOption } from '../../../../services/pairings/pairings.service';

@Component({
  selector: 'app-pairings-swap',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './pairings-swap.component.html',
  styleUrls: ['./pairings-swap.component.scss']
})
export class PairingsSwapComponent {
  private readonly pairingsService = inject(PairingsService);

  readonly playerOptions: Signal<PairingsSwapPlayerOption[]> = this.pairingsService.swapPlayerOptions;
  readonly groupOptions: Signal<PairingsSwapGroupOption[]> = this.pairingsService.swapGroupOptions;
  readonly swapHistory: Signal<PairingsSwapHistoryEntry[]> = this.pairingsService.swapHistory;

  readonly isBusy: Signal<boolean> = computed(() => this.pairingsService.state().isLoading || this.pairingsService.state().isGenerating);

  readonly selectedPlayerA = signal<string | null>(null);
  readonly selectedPlayerB = signal<string | null>(null);
  readonly selectedGroupA = signal<string | null>(null);
  readonly selectedGroupB = signal<string | null>(null);

  readonly selectedPlayerADetail = computed(() => this.resolvePlayer(this.selectedPlayerA()));
  readonly selectedPlayerBDetail = computed(() => this.resolvePlayer(this.selectedPlayerB()));
  readonly selectedGroupADetail = computed(() => this.resolveGroup(this.selectedGroupA()));
  readonly selectedGroupBDetail = computed(() => this.resolveGroup(this.selectedGroupB()));

  readonly canSwapPlayers = computed(() => {
    const a = this.selectedPlayerA();
    const b = this.selectedPlayerB();
    return Boolean(a && b && a !== b && !this.isBusy());
  });

  readonly canSwapGroups = computed(() => {
    const a = this.selectedGroupA();
    const b = this.selectedGroupB();
    return Boolean(a && b && a !== b && !this.isBusy());
  });

  constructor() {
    effect(() => {
      if (this.playerOptions().length === 0) {
        this.selectedPlayerA.set(null);
        this.selectedPlayerB.set(null);
      }
      if (this.groupOptions().length === 0) {
        this.selectedGroupA.set(null);
        this.selectedGroupB.set(null);
      }
    });
  }

  swapPlayers(): void {
    const first = this.selectedPlayerA();
    const second = this.selectedPlayerB();

    if (!first || !second || first === second) {
      return;
    }

    this.pairingsService.swapPlayers(first, second).subscribe({
      next: () => {
        this.selectedPlayerA.set(null);
        this.selectedPlayerB.set(null);
      }
    });
  }

  swapGroups(): void {
    const first = this.selectedGroupA();
    const second = this.selectedGroupB();

    if (!first || !second || first === second) {
      return;
    }

    this.pairingsService.swapGroups(first, second).subscribe({
      next: () => {
        this.selectedGroupA.set(null);
        this.selectedGroupB.set(null);
      }
    });
  }

  trackByPlayer = (_: number, item: PairingsSwapPlayerOption) => item.slotId;
  trackByGroup = (_: number, item: PairingsSwapGroupOption) => item.groupId;
  trackByHistory = (_: number, item: PairingsSwapHistoryEntry) => item.id;

  private resolvePlayer(slotId: string | null): PairingsSwapPlayerOption | null {
    if (!slotId) {
      return null;
    }
    return this.playerOptions().find(option => option.slotId === slotId) ?? null;
  }

  private resolveGroup(groupId: string | null): PairingsSwapGroupOption | null {
    if (!groupId) {
      return null;
    }
    return this.groupOptions().find(option => option.groupId === groupId) ?? null;
  }
}
